import React from 'react';
import initializeComponent from '../../Shared/react-initialize';
import { withTheme } from './themes';
import { trackEvent } from '../../Shared/utils.js';
import {
   color,
   breakpoint,
   borderRadius,
   space,
   fontSize,
   fontWeight,
   lineHeight,
} from '@core-ds/primitives';
import { ArrowRight } from '@core-ds/icons/16';
import { withBreakpoints, withBreakpointsContext, BreakpointsContext } from 'core-components';
import styled from 'styled-components';

const CampaignStyleContainer = styled.div`
   ${props => props.css}
`;
const CampaignContainer = withBreakpoints(styled.div`
   height: ${props => (props.breakpoints.isMobile() ? '350px' : '200px')};
   background: ${color.gray1};
`);

const TextContainer = withBreakpoints(styled.div`
   display: flex;
   flex-direction: column;
   max-width: ${props => (props.breakpoints.isMobile() ? '100%' : '66%')};
`);

const CampaignBannerTitle = withBreakpoints(styled.div`
   font-family: 'Proxima Nova', 'Archivo Black', sans-serif;
   font-style: normal;
   font-weight: ${fontWeight.normal};
   font-size: ${fontSize[7]};
   letter-spacing: -0.02em;
   line-height: ${lineHeight.none};
`);

const CampaignBannerSubtitle = withBreakpoints(styled.div`
   font-family: 'Proxima Nova', 'Lato', Arial, sans-serif;
   font-style: normal;
   font-weight: ${fontWeight.normal};
   font-size: ${fontSize[5]};
   margin: ${props => (props.breakpoints.isMobileTablet() ? space[3] : space[0])} 0;
`);

const CampaignBannerButton = styled.a`
   color: ${props => props.theme.fontColor};
   background: ${props => props.theme.buttonColor};
   font-family: Lato;
   font-style: normal;
   font-size: ${fontSize[2]};
   font-weight: ${fontWeight.bold};
   line-height: ${lineHeight.loose};

   border-radius: ${borderRadius.md};
   text-align: center;
   padding: ${space[2]} ${space[4]};

   &:hover {
      background-color: ${props => props.theme.buttonHoverColor};
      text-decoration: none;
   }

   &:visited {
      color: ${props => props.theme.fontColor};
   }

   margin-top: ${space[3]};
`;

const ButtonArrow = styled(ArrowRight)`
   color: ${props => props.theme.fontColor};
   vertical-align: middle;
   margin-left: 10px;
`;

const NewText = styled.div`
   font-family: Lato;
   font-style: normal;
   font-weight: ${fontWeight.bold};
   font-size: ${fontSize[1]};
   line-height: ${lineHeight.tight};
   letter-spacing: 1px;
   text-transform: uppercase;
   color: ${color.blue};
`;

const Wrapper = withBreakpoints(styled.div`
   position: relative;
   display: flex;
   height: 100%;
   background-image: url(${props => props.imageUrl});
   background-repeat: no-repeat;
   background-position-x: ${props => props.cssBackgroundPositionX};
   background-position-y: bottom;
   background-size: cover;
   align-items: flex-start;

   flex-wrap: wrap;

   &:first-child {
      padding-left: ${props => (props.breakpoints.isMobileTablet() ? space[4] : space[5])};
   }

   &:last-child {
      padding-right: ${props => (props.breakpoints.isMobileTablet() ? space[4] : space[5])};
   }

   flex-direction: column;
   padding-top: ${space[3]};
   padding-bottom: ${space[3]};
   justify-content: center;
`);

const CampaignGraphic = props => {
   const {
      cssBackgroundPositionX,
      imageUrl,
      bannerTitle,
      bannerSubTitle,
      buttonText,
      bannerLink,
   } = props;
   const onClick = () => trackEvent('Major Campaign Banner', 'Click', bannerTitle);

   return (
      <Wrapper
         imageUrl={imageUrl}
         cssBackgroundPositionX={cssBackgroundPositionX}
         className="campaign-banner-inline"
      >
         <TextContainer className="campaign-text">
            <NewText className="campaign-new">{_js('NEW')}</NewText>
            <CampaignBannerTitle className="campaign-title">{bannerTitle}</CampaignBannerTitle>
            <CampaignBannerSubtitle className="campaign-subtitle">
               {bannerSubTitle}
            </CampaignBannerSubtitle>
         </TextContainer>
         <CampaignBannerButton href={bannerLink} onClick={onClick} className="campaign-button">
            {buttonText}
            <ButtonArrow />
         </CampaignBannerButton>
      </Wrapper>
   );
};

const getGraphicProps = props => {
   const breakpoints = React.useContext(BreakpointsContext);

   const { buttonText, bannerLink, desktop, mobile } = props;

   const useMobileData = breakpoints.isMobileTablet() || breakpoints.isMobile();

   const graphicProps = useMobileData ? mobile : desktop;

   const { bannerGraphic } = graphicProps;
   const imageUrl = bannerGraphic;

   return {
      ...graphicProps,
      imageUrl,
      bannerLink,
      buttonText,
   };
};

const MajorCampaignBanner = props => {
   const graphicProps = getGraphicProps(props);

   return (
      <CampaignStyleContainer css={props.css}>
         <CampaignContainer className="campaign-container-guide">
            <CampaignGraphic {...graphicProps} />
         </CampaignContainer>
      </CampaignStyleContainer>
   );
};

const ThemedMajorCampaignBanner = withBreakpointsContext(withTheme(MajorCampaignBanner));

initializeComponent('MajorCampaignBannerInline', ThemedMajorCampaignBanner);
